/**
 * The mobility session-token store (decision M-1: SPA tokens live in
 * `sessionStorage`, short TTL, NEVER localStorage — closed when the tab closes).
 *
 * The token is the bank RS256 JWT (Thrive / talent-marketplace) captured from
 * `?JWT_TOKEN=` or returned by `POST /auth/sso/exchange`. OTP verify may mint a
 * test-only RS256 token in the test suite.
 * `lib/api.ts` reads it here to set `Authorization: Bearer <token>` on every
 * call; a 401 clears it (session expired / role revoked / roster inactive) and
 * the route guards bounce to /login.
 *
 * A tiny synchronous pub/sub lets the session hook react to a logout that
 * happens deep inside the api client (a 401 mid-request) without prop-drilling —
 * `subscribe(fn)` fires on every set/clear so `useSession` can re-resolve.
 *
 * NEVER-LOG-PII / NEVER-LOG-TOKENS: the raw token is never logged anywhere.
 */

const STORAGE_KEY = 'thrive_mobility_token'

type Listener = () => void
const listeners = new Set<Listener>()

/** Guard against SSR / non-browser runtimes (tests run in jsdom, which has it). */
function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

/** The current session token, or `null` when signed out. */
export function getToken(): string | null {
  return storage()?.getItem(STORAGE_KEY) ?? null
}

/** Persist a session token and notify subscribers. */
export function setToken(token: string): void {
  storage()?.setItem(STORAGE_KEY, token)
  emit()
}

/**
 * Write the token WITHOUT notifying subscribers. Used when the parent layout
 * must put the JWT in storage during render (so child guards see `getToken()`)
 * before a layout-effect `setToken` emits and `/auth/me` runs.
 */
export function stashToken(token: string): void {
  storage()?.setItem(STORAGE_KEY, token)
}

/** Clear the session token (logout / a 401) and notify subscribers. */
export function clearToken(): void {
  storage()?.removeItem(STORAGE_KEY)
  emit()
}

/** True when a token is present (a cheap "maybe signed in" probe; not a validity check). */
export function hasToken(): boolean {
  return getToken() !== null
}

/** Subscribe to token changes (set/clear). Returns an unsubscribe fn. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function emit(): void {
  for (const listener of listeners) listener()
}
